import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';

/**
 * Service for managing 3D building models in Mapbox
 */
class BuildingModelsService {
  constructor() {
    this.models = new Map(); // Cache loaded models
    this.loadedBuildings = new Set(); // Track what's currently on map
    this.loader = new GLTFLoader();
    this.scene = null;
    this.camera = null;
    this.renderer = null;
  }

  /**
   * Famous buildings with their 3D model configurations
   */
  getFamousBuildings() {
    return [
      {
        id: 'eiffel-tower',
        name: 'Eiffel Tower',
        coordinates: [2.2934, 48.8575], // [lng, lat]
        modelUrl: '/models/eiffel-tower.glb',
        scale: { x: 2.2, y: 2.2, z: 2.2 },
        rotation: { x: 90, y: 45, z: 0 },
        minZoom: 14,
        country: 'France',
        city: 'Paris'
      },
      {
        id: 'statue-of-liberty',
        name: 'Statue of Liberty',
        coordinates: [-74.0445, 40.6892],
        modelUrl: '/models/statue-of-liberty.glb',
        scale: { x: 1, y: 1, z: 1 },
        rotation: { x: 0, y: 0, z: 0 },
        minZoom: 15,
        country: 'United States',
        city: 'New York'
      },
      {
        id: 'big-ben',
        name: 'Big Ben',
        coordinates: [-0.1246, 51.4994],
        modelUrl: '/models/big-ben.glb',
        scale: { x: 1, y: 1, z: 1 },
        rotation: { x: 0, y: 0, z: 0 },
        minZoom: 15,
        country: 'United Kingdom',
        city: 'London'
      },
      {
        id: 'colosseum',
        name: 'Colosseum',
        coordinates: [12.4924, 41.8902],
        modelUrl: '/models/colosseum.glb',
        scale: { x: 1, y: 1, z: 1 },
        rotation: { x: 0, y: 0, z: 0 },
        minZoom: 15,
        country: 'Italy',
        city: 'Rome'
      },
      {
        id: 'sydney-opera-house',
        name: 'Sydney Opera House',
        coordinates: [151.2153, -33.8568],
        modelUrl: '/models/sydney-opera-house.glb',
        scale: { x: 1, y: 1, z: 1 },
        rotation: { x: 0, y: 0, z: 0 },
        minZoom: 15,
        country: 'Australia',
        city: 'Sydney'
      },
      {
        id: 'taj-mahal',
        name: 'Taj Mahal',
        coordinates: [78.0421, 27.1751],
        modelUrl: '/models/taj-mahal.glb',
        scale: { x: 1, y: 1, z: 1 },
        rotation: { x: 0, y: 0, z: 0 },
        minZoom: 15,
        country: 'India',
        city: 'Agra'
      }
    ];
  }

  /**
   * Get buildings within map bounds
   */
  getBuildingsInBounds(bounds, zoom) {
    const buildings = this.getFamousBuildings();
    
    return buildings.filter(building => {
      const [lng, lat] = building.coordinates;
      
      // Check if building is within bounds
      const inBounds = lng >= bounds.getWest() && 
                      lng <= bounds.getEast() && 
                      lat >= bounds.getSouth() && 
                      lat <= bounds.getNorth();
      
      // Check zoom level
      const zoomOk = zoom >= building.minZoom;
      
      return inBounds && zoomOk;
    });
  }

  /**
   * Load a 3D model
   */
  async loadModel(modelUrl) {
    if (this.models.has(modelUrl)) {
      return this.models.get(modelUrl).clone();
    }

    try {
      const gltf = await new Promise((resolve, reject) => {
        this.loader.load(
          modelUrl,
          resolve,
          (progress) => {
            console.log(`Loading ${modelUrl}: ${(progress.loaded / progress.total * 100)}%`);
          },
          reject
        );
      });

      // Cache the model
      this.models.set(modelUrl, gltf.scene);
      
      return gltf.scene.clone();
    } catch (error) {
      console.error(`Failed to load 3D model: ${modelUrl}`, error);
      return null;
    }
  }

  /**
   * Create a simple fallback model if GLB fails to load
   */
  createFallbackModel() {
    const geometry = new THREE.BoxGeometry(50, 50, 100);
    const material = new THREE.MeshBasicMaterial({ 
      color: 0x888888,
      transparent: true,
      opacity: 0.8
    });
    const cube = new THREE.Mesh(geometry, material);
    
    // Add a simple label
    const textGeometry = new THREE.PlaneGeometry(100, 20);
    const textMaterial = new THREE.MeshBasicMaterial({ 
      color: 0xffffff,
      transparent: true,
      opacity: 0.9
    });
    const textPlane = new THREE.Mesh(textGeometry, textMaterial);
    textPlane.position.set(0, 0, 60);
    
    const group = new THREE.Group();
    group.add(cube);
    group.add(textPlane);
    
    return group;
  }

  /**
   * Apply transformations to model
   */
  transformModel(model, building) {
    // Apply scale
    if (building.scale) {
      model.scale.set(
        building.scale.x || 1,
        building.scale.y || 1,
        building.scale.z || 1
      );
    }

    // Apply rotation
    if (building.rotation) {
      model.rotation.set(
        THREE.MathUtils.degToRad(building.rotation.x || 0),
        THREE.MathUtils.degToRad(building.rotation.y || 0),
        THREE.MathUtils.degToRad(building.rotation.z || 0)
      );
    }

    return model;
  }

  /**
   * Check if model exists (for development)
   */
  async checkModelExists(modelUrl) {
    try {
      const response = await fetch(modelUrl, { method: 'HEAD' });
      return response.ok;
    } catch (error) {
      return false;
    }
  }

  /**
   * Get building by coordinates (for click detection)
   */
  getBuildingAt(lng, lat, tolerance = 0.001) {
    return this.getFamousBuildings().find(building => {
      const [bLng, bLat] = building.coordinates;
      return Math.abs(bLng - lng) < tolerance && Math.abs(bLat - lat) < tolerance;
    });
  }

  /**
   * Clear all cached models (for memory management)
   */
  clearCache() {
    this.models.clear();
    this.loadedBuildings.clear();
  }
}

export default new BuildingModelsService();